import React, { useState, useRef } from "react";
import { Popover, TextField, Box, IconButton } from "@mui/material";
import { FiFilter } from "react-icons/fi";
import "./FilterPopover.scss";

interface FilterPopoverProps {
  column: string;
  filterType: "text" | "date";
  currentValue: string;
  onFilterChange: (column: string, value: string, type: "text" | "date") => void;
  onClear: (column: string) => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  column,
  filterType,
  currentValue,
  onFilterChange,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localValue, setLocalValue] = useState(currentValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setLocalValue(currentValue);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (localValue.trim()) {
      onFilterChange(column, localValue.trim(), filterType);
    } else {
      onClear(column);
    }
    handleClose();
  };

  const handleClear = () => {
    setLocalValue("");
    onClear(column);
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleApply();
    if (e.key === "Escape") handleClose();
  };

  const isActive = currentValue !== "";

  return (
    <>
      <IconButton
        className={`filter-icon-btn ${isActive ? "active" : ""}`}
        onClick={handleOpen}
        size="small"
      >
        <FiFilter size={14} />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        className="filter-popover"
        slotProps={{
          paper: {
            className: "filter-popover-paper",
          },
        }}
      >
        <Box className="filter-popover-content">
          {filterType === "date" ? (
            <TextField
              inputRef={inputRef}
              type="date"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              className="filter-input"
            />
          ) : (
            <TextField
              inputRef={inputRef}
              type="text"
              placeholder="Filter..."
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              className="filter-input"
            />
          )}

          <Box className="filter-actions">
            <button className="filter-btn clear-btn" onClick={handleClear}>
              Clear
            </button>
            <button className="filter-btn apply-btn" onClick={handleApply}>
              Apply
            </button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default FilterPopover;
